/**
 * Persistent Message Queue using IndexedDB
 * 
 * This module provides a reliable message queue that persists across app restarts.
 * It handles message retry logic with exponential backoff and deduplication.
 */

import type { MessageReplyTo } from '@/types/models'
import type { SenderType } from '@/lib/chatData'

// Database configuration
const DB_NAME = 'GetMoreTatsMessageQueue'
const DB_VERSION = 1
const STORE_NAME = 'pendingMessages'

// Retry configuration
const INITIAL_RETRY_DELAY = 1000 // 1 second
const MAX_RETRY_DELAY = 30000 // 30 seconds
const MAX_RETRY_ATTEMPTS = 5

export interface PendingMessage {
  clientGeneratedId: string
  chatId: string
  content: string
  senderId: string
  senderType: SenderType
  type: 'text' | 'image'
  imageUrl?: string
  timestamp: Date
  attempts: number
  nextRetryAt: Date
  lastError?: string
  createdAt: Date
  // Flash metadata
  metadata?: {
    flashId?: string
    minPrice?: string
    minSize?: string
    isFlash?: boolean
  }
  replyTo?: MessageReplyTo
}

class MessageQueue {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    // Return existing initialization if in progress
    if (this.initPromise) return this.initPromise

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('[MessageQueue] Failed to open database:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        console.log('[MessageQueue] Database opened successfully')
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'clientGeneratedId' })
          
          // Create indexes for efficient queries
          store.createIndex('chatId', 'chatId', { unique: false })
          store.createIndex('nextRetryAt', 'nextRetryAt', { unique: false })
          store.createIndex('attempts', 'attempts', { unique: false })
          
          console.log('[MessageQueue] Object store created')
        }
      }
    })

    return this.initPromise
  }

  /**
   * Ensure database is initialized before operations
   */
  private async ensureInit(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init()
    }
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    return this.db
  }

  /**
   * Add a message to the queue
   */
  async enqueue(message: PendingMessage): Promise<void> {
    const db = await this.ensureInit()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      
      const request = store.put(message)
      
      request.onsuccess = () => {
        console.log('[MessageQueue] Message enqueued:', message.clientGeneratedId)
        resolve()
      }
      
      request.onerror = () => {
        console.error('[MessageQueue] Failed to enqueue message:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Remove a message from the queue (after successful send)
   */
  async dequeue(clientGeneratedId: string): Promise<void> {
    const db = await this.ensureInit()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      
      const request = store.delete(clientGeneratedId)
      
      request.onsuccess = () => {
        console.log('[MessageQueue] Message dequeued:', clientGeneratedId)
        resolve()
      }
      
      request.onerror = () => {
        console.error('[MessageQueue] Failed to dequeue message:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Update a message in the queue (e.g., after failed attempt)
   */
  async update(message: PendingMessage): Promise<void> {
    return this.enqueue(message) // Put operation updates if key exists
  }

  /**
   * Get a specific message by clientGeneratedId
   */
  async get(clientGeneratedId: string): Promise<PendingMessage | null> {
    const db = await this.ensureInit()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      
      const request = store.get(clientGeneratedId)
      
      request.onsuccess = () => {
        resolve(request.result || null)
      }
      
      request.onerror = () => {
        console.error('[MessageQueue] Failed to get message:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Get all messages for a specific chat
   */
  async getForChat(chatId: string): Promise<PendingMessage[]> {
    const db = await this.ensureInit()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('chatId')
      
      const request = index.getAll(chatId)
      
      request.onsuccess = () => {
        resolve(request.result || [])
      }
      
      request.onerror = () => {
        console.error('[MessageQueue] Failed to get messages for chat:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Get all messages ready for retry (nextRetryAt <= now)
   */
  async getReadyForRetry(): Promise<PendingMessage[]> {
    const db = await this.ensureInit()
    const now = new Date()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      
      const request = store.getAll()
      
      request.onsuccess = () => {
        const allMessages = request.result || []
        const readyMessages = allMessages.filter(msg => 
          new Date(msg.nextRetryAt) <= now && msg.attempts < MAX_RETRY_ATTEMPTS
        )
        resolve(readyMessages)
      }
      
      request.onerror = () => {
        console.error('[MessageQueue] Failed to get messages ready for retry:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Get all permanently failed messages (exceeded max retry attempts)
   */
  async getFailedMessages(): Promise<PendingMessage[]> {
    const db = await this.ensureInit()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      
      const request = store.getAll()
      
      request.onsuccess = () => {
        const allMessages = request.result || []
        const failedMessages = allMessages.filter(msg => msg.attempts >= MAX_RETRY_ATTEMPTS)
        resolve(failedMessages)
      }
      
      request.onerror = () => {
        console.error('[MessageQueue] Failed to get failed messages:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Count pending messages for a chat
   */
  async countForChat(chatId: string): Promise<number> {
    const messages = await this.getForChat(chatId)
    return messages.length
  }

  /**
   * Clear all messages (use with caution)
   */
  async clear(): Promise<void> {
    const db = await this.ensureInit()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      
      const request = store.clear()
      
      request.onsuccess = () => {
        console.log('[MessageQueue] Queue cleared')
        resolve()
      }
      
      request.onerror = () => {
        console.error('[MessageQueue] Failed to clear queue:', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Calculate next retry time with exponential backoff
   */
  calculateNextRetry(attempts: number): Date {
    const delay = Math.min(
      INITIAL_RETRY_DELAY * Math.pow(2, attempts),
      MAX_RETRY_DELAY
    )
    return new Date(Date.now() + delay)
  }

  /**
   * Check if a message has exceeded max retry attempts
   */
  hasExceededMaxRetries(attempts: number): boolean {
    return attempts >= MAX_RETRY_ATTEMPTS
  }
}

// Export singleton instance
export const messageQueue = new MessageQueue()

/**
 * Helper function to create a pending message
 */
export function createPendingMessage(
  clientGeneratedId: string,
  chatId: string,
  content: string,
  senderId: string,
  senderType: SenderType,
  type: 'text' | 'image',
  imageUrl?: string,
  metadata?: {
    flashId?: string
    minPrice?: string
    minSize?: string
    isFlash?: boolean
  },
  replyTo?: MessageReplyTo
): PendingMessage {
  const now = new Date()
  return {
    clientGeneratedId,
    chatId,
    content,
    senderId,
    senderType,
    type,
    imageUrl,
    timestamp: now,
    attempts: 0,
    nextRetryAt: now, // Ready to send immediately
    createdAt: now,
    metadata,
    ...(replyTo && { replyTo }),
  }
}

/**
 * Helper function to mark a message for retry
 */
export function markForRetry(
  message: PendingMessage,
  error: string
): PendingMessage {
  const newAttempts = message.attempts + 1
  return {
    ...message,
    attempts: newAttempts,
    nextRetryAt: messageQueue.calculateNextRetry(newAttempts),
    lastError: error
  }
}
